export type Volume = {
  numeral: string;
  title: string;
  subtitle: string;
  intro: string;
  inside: string[];
  objective: string;
  note: string;
  accent: string;
};

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'The Obesity Assessment Blueprint™',
    subtitle: 'Organize the Initial Clinical Assessment',
    intro:
      'A practical reference for organizing the initial assessment of adults presenting with obesity or weight-related concerns.',
    inside: [
      'A structured overview of the initial clinical assessment.',
      'A patient history and weight trajectory organizer.',
      'A worksheet for documenting relevant health information.',
      'A framework for organizing potential obesity-related complications.',
      'A checklist for reviewing relevant medical history and existing conditions.',
      'A patient-centered assessment worksheet.',
      'A template for recording outstanding questions.',
      'A guide to organizing information that requires further evaluation.',
    ],
    objective:
      'Help you organize the information relevant to an initial obesity assessment without having to build your own framework from scratch.',
    note: 'The material supports clinical organization and does not replace individualized assessment or applicable diagnostic criteria.',
    accent: '#1D63D8',
  },
  {
    numeral: 'II',
    title: 'The Metabolic & Comorbidity Blueprint™',
    subtitle: 'Organize the Review of Obesity-Related Health Conditions',
    intro:
      'Obesity can coexist with several conditions that require consideration within an individualized care plan. This volume provides a structured way to organize those considerations.',
    inside: [
      'A metabolic health review framework.',
      'A worksheet for organizing relevant laboratory results.',
      'A cardiovascular risk information organizer.',
      'A checklist for documenting existing comorbidities.',
      'A framework for reviewing sleep-related and respiratory considerations.',
      'A worksheet for organizing musculoskeletal and functional concerns.',
      'A patient history review template.',
      'A reference checklist for identifying topics requiring further evaluation.',
    ],
    objective: 'Give obesity-related health considerations a clear place within your consultation workflow.',
    note: "Clinical interpretation and subsequent actions must be based on the patient's circumstances, current evidence, and applicable guidelines.",
    accent: '#15855A',
  },
  {
    numeral: 'III',
    title: 'The Weight Management Planning Blueprint™',
    subtitle: 'Structure Individualized Treatment Reviews',
    intro:
      'Obesity care is not a one-size-fits-all process. The relevant considerations may differ according to medical history, health status, patient preferences, previous treatment experiences, and individual goals.',
    inside: [
      'A framework for documenting individualized care goals.',
      'A patient preferences and priorities worksheet.',
      'A treatment history organizer.',
      'A matrix for comparing categories of weight management interventions.',
      'A worksheet for organizing behavioral and lifestyle considerations.',
      'A reference framework for documenting pharmacological treatment considerations.',
      'A checklist for recording questions requiring further clinical review.',
      'A shared decision-making worksheet.',
    ],
    objective:
      'Help you structure treatment discussions and document relevant considerations without relying on memory alone.',
    note: "Treatment selection remains an individualized professional decision informed by current guidance and the patient's circumstances.",
    accent: '#D9620F',
  },
  {
    numeral: 'IV',
    title: 'The Advanced Obesity Care Blueprint™',
    subtitle: 'Organize Complex Cases and Additional Treatment Considerations',
    intro:
      'Some patients require a more detailed review because of multiple conditions, previous treatment experiences, or additional clinical considerations.',
    inside: [
      'A complex case assessment worksheet.',
      'A framework for organizing relevant medical history.',
      'A review checklist for coexisting conditions.',
      'A template for documenting previous treatment experiences.',
      'A treatment considerations matrix.',
      'A worksheet for recording contraindication and precaution questions that require professional verification.',
      'A reference-tracking sheet.',
      'A checklist for organizing potential referral considerations.',
    ],
    objective:
      'Help you organize complex obesity-related consultations and identify questions that require further evaluation or specialist input.',
    note: 'The material does not independently determine treatment eligibility or replace clinical assessment.',
    accent: '#C8243A',
  },
  {
    numeral: 'V',
    title: 'The Obesity Treatment Review Blueprint™',
    subtitle: 'Organize Treatment Reassessment and Monitoring',
    intro:
      'Starting a treatment plan is only one part of ongoing obesity care. Clinical reviews may also require the organization of progress information, treatment experiences, patient concerns, tolerability, and other relevant factors.',
    inside: [
      'A treatment review worksheet.',
      'A patient-reported progress organizer.',
      'A template for documenting treatment experiences.',
      'A checklist for organizing tolerability and safety considerations.',
      'A framework for documenting questions about treatment continuation or modification.',
      'A patient goals review sheet.',
      'A reference verification checklist.',
      'A structured consultation documentation template.',
    ],
    objective: 'Make treatment reviews easier to organize, document, and prepare.',
    note: 'Clinical decisions about treatment modification must be based on individualized assessment, professional judgment, and current guidance.',
    accent: '#6D3FC9',
  },
  {
    numeral: 'VI',
    title: 'The Long-Term Obesity Care Blueprint™',
    subtitle: 'Organize Follow-Up, Continuity, and Ongoing Care',
    intro:
      'Obesity care often extends beyond a single consultation. A consistent organizational framework can help professionals prepare future reviews, record outstanding questions, and maintain a clearer overview of the care process.',
    inside: [
      'A long-term follow-up planning framework.',
      'A periodic review checklist.',
      'A patient goals reassessment worksheet.',
      'A template for documenting outstanding issues.',
      'A follow-up preparation sheet.',
      'A framework for organizing relevant health indicators.',
      'A continuity-of-care documentation template.',
      'A worksheet for preparing the next consultation.',
    ],
    objective: 'Help you establish a more structured process for organizing ongoing obesity care.',
    note: "Follow-up intervals and clinical monitoring requirements must be individualized according to the patient's circumstances and applicable professional guidance.",
    accent: '#0E7490',
  },
];
